import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  Lightbulb, ArrowUp, Sparkle, Plus, X, Check, Hourglass, Tag, Clock,
} from "@phosphor-icons/react";

const CATEGORIES = [
  { id: "agenda",   label: "Agenda · CRM" },
  { id: "annunci",  label: "Annunci & Foto" },
  { id: "foto",     label: "Foto" },
  { id: "portali",  label: "Portali & Feed" },
  { id: "live",     label: "Live & Sopralluoghi" },
  { id: "altro",    label: "Altro" },
];

const STATUS_META = {
  voting: { label: "In votazione", color: "bg-amber-100 text-amber-800", icon: ArrowUp },
  in_progress: { label: "In sviluppo", color: "bg-blue-100 text-blue-800", icon: Hourglass },
  done: { label: "Fatto", color: "bg-green-100 text-green-800", icon: Check },
  rejected: { label: "Non per ora", color: "bg-gray-100 text-gray-700", icon: X },
};

export default function Idee() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("voting");
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/ideas", { params });
      setIdeas(data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const onVote = async (id) => {
    try {
      const { data } = await api.post(`/ideas/${id}/vote`);
      setIdeas((prev) => prev.map((it) => (it.id === id ? { ...it, votes: data.votes, voted_by_me: data.voted_by_me } : it)));
    } catch (e) {
      toast.error("Errore nel voto");
    }
  };

  const onCreated = (newIdea) => {
    setShowForm(false);
    setIdeas((prev) => [newIdea, ...prev]);
    if (newIdea.bonus_credits_granted) {
      toast.success(`Grazie! +${newIdea.bonus_credits_granted} crediti per la tua prima idea proposta.`, { duration: 6000 });
    } else {
      toast.success("Idea proposta. Adesso parola agli altri agenti — più voti = più visibilità.");
    }
  };

  const onDelete = (id) => {
    // iOS PWA blocks window.confirm in standalone mode → use sonner action toast
    toast(
      "Eliminare questa idea?",
      {
        description: "Non si può annullare.",
        action: {
          label: "Elimina",
          onClick: async () => {
            try {
              await api.delete(`/ideas/${id}`);
              setIdeas((prev) => prev.filter((it) => it.id !== id));
              toast.success("Idea eliminata");
            } catch { toast.error("Errore"); }
          },
        },
        cancel: { label: "Annulla", onClick: () => {} },
        duration: 8000,
      },
    );
  };

  return (
    <AppShell title="Idee dagli agenti" breadcrumb="Idee">
      {/* Hero */}
      <div className="mb-8 max-w-3xl">
        <span className="overline">Comunità</span>
        <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-tight tracking-tight">
          La costruiamo<br/>
          <span className="italic-accent text-[#7C8B6E]">insieme.</span>
        </h1>
        <p className="mt-4 text-[15px] text-[#6B6A65] leading-relaxed">
          Pronto è pensata da chi fa il lavoro vero. Se ti manca qualcosa, dillo qui:
          gli altri agenti votano e quello che convince di più finisce in alto alla lista.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[13px]">
          <div className="card-flat p-3 bg-amber-50 border-amber-200 text-amber-900 flex items-start gap-2.5">
            <Sparkle size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
            <span><strong>+5 crediti</strong> la prima volta che pubblichi un&apos;idea</span>
          </div>
          <div className="card-flat p-3 bg-amber-50 border-amber-200 text-amber-900 flex items-start gap-2.5">
            <Sparkle size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
            <span><strong>+30 crediti</strong> se il team la mette in sviluppo</span>
          </div>
          <div className="card-flat p-3 bg-amber-50 border-amber-200 text-amber-900 flex items-start gap-2.5">
            <Sparkle size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
            <span><strong>+30 crediti</strong> se la tua idea raggiunge 10 voti</span>
          </div>
        </div>
        <p className="mt-3 text-[11.5px] text-[#9A968B] leading-snug max-w-2xl">
          Le idee servono a far crescere Pronto. Ogni proposta viene letta dal team: se è generica, ripetuta o non utile NON viene approvata e i bonus successivi non vengono dati. Max 3 idee a settimana per utente.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 pb-4 border-b border-[#E8E5DE]">
        <div className="flex items-center gap-1 text-[12px] flex-wrap">
          {[
            { id: "voting", label: "In votazione" },
            { id: "in_progress", label: "In sviluppo" },
            { id: "done", label: "Fatte" },
            { id: "all", label: "Tutte" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1.5 rounded-md transition-colors ${
                filter === f.id ? "bg-[#1C1C1A] text-white" : "bg-white border border-[#E8E5DE] hover:border-[#C8C5BD] text-[#1C1C1A]"
              }`}
              data-testid={`filter-${f.id}`}
            >{f.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary text-sm"
            data-testid="propose-idea-btn"
          >
            <Plus size={14} weight="bold" /> Proponi un'idea
          </button>
        </div>
      </div>

      {/* List */}
      {loading && <div className="py-10 text-center text-[#9A968B]">Caricamento…</div>}
      {!loading && ideas.length === 0 && (
        <div className="py-12 text-center" data-testid="ideas-empty">
          <Lightbulb size={48} weight="duotone" className="mx-auto text-[#C8C5BD] mb-3" />
          <p className="text-[#6B6A65] text-[14px]">
            {filter === "voting" ? "Nessuna idea in votazione. Sii il primo a proporne una." :
             filter === "in_progress" ? "Niente in sviluppo al momento." :
             filter === "done" ? "Nessuna idea ancora completata." :
             "Nessuna idea trovata."}
          </p>
        </div>
      )}
      <div className="space-y-2.5">
        {ideas.map((idea) => (
          <IdeaCard key={idea.id} idea={idea} onVote={onVote} onDelete={onDelete} />
        ))}
      </div>

      {showForm && <ProposeForm onClose={() => setShowForm(false)} onCreated={onCreated} />}
    </AppShell>
  );
}

function IdeaCard({ idea, onVote, onDelete }) {
  const meta = STATUS_META[idea.status] || STATUS_META.voting;
  const StatusIcon = meta.icon;
  return (
    <div className="card-flat bg-white p-4 sm:p-5 flex gap-4 items-start" data-testid={`idea-${idea.id}`}>
      <button
        onClick={() => onVote(idea.id)}
        className={`shrink-0 w-12 sm:w-14 flex flex-col items-center justify-center py-2.5 rounded-md border transition-all ${
          idea.voted_by_me
            ? "bg-[#7C8B6E] text-white border-[#7C8B6E]"
            : "bg-white border-[#E8E5DE] hover:border-[#7C8B6E] text-[#1C1C1A]"
        }`}
        data-testid={`vote-${idea.id}`}
        aria-label={idea.voted_by_me ? "Togli voto" : "Vota"}
      >
        <ArrowUp size={16} weight="bold" />
        <span className="text-[14px] font-bold tabular-nums mt-0.5">{idea.votes}</span>
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-bold px-1.5 py-0.5 rounded ${meta.color}`}>
            <StatusIcon size={10} weight="bold" /> {meta.label}
          </span>
          {idea.category && idea.category !== "altro" && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#6B6A65]">
              <Tag size={10} /> {idea.category}
            </span>
          )}
          {idea.is_mine && (
            <span className="inline-flex items-center text-[10px] uppercase tracking-[0.1em] text-[#7C8B6E] font-semibold">la tua idea</span>
          )}
        </div>
        <h3 className="font-serif text-[17px] sm:text-[19px] mt-1.5 text-[#1C1C1A] leading-snug">{idea.title}</h3>
        <p className="text-[13.5px] text-[#1C1C1A]/85 mt-1.5 leading-relaxed whitespace-pre-wrap">{idea.body}</p>
        {idea.response_note && (
          <div className="mt-3 p-2.5 bg-[#F1F4ED] border-l-2 border-[#7C8B6E] text-[12.5px] text-[#1C1C1A]">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#7C8B6E] font-semibold mb-1">Risposta dal team Pronto</div>
            {idea.response_note}
          </div>
        )}
        <div className="mt-2.5 flex items-center justify-between text-[11px] text-[#9A968B]">
          <span className="inline-flex items-center gap-1.5">
            <Clock size={10} /> {idea.author_name}
          </span>
          {idea.is_mine && (
            <button onClick={() => onDelete(idea.id)} className="hover:text-red-600 transition-colors" data-testid={`delete-${idea.id}`}>
              elimina
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProposeForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", body: "", category: "altro" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 15) {
      toast.error("Il titolo deve avere almeno 15 caratteri (sii specifico)");
      return;
    }
    if (form.body.trim().length < 80) {
      toast.error("Aggiungi qualche dettaglio in più — almeno 80 caratteri. Spiega cosa vorresti e in che situazione ti servirebbe.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ideas", form);
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio idea");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="propose-modal">
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E8E5DE] flex items-center justify-between">
          <h3 className="font-editorial text-xl">Proponi un'idea</h3>
          <button type="button" onClick={onClose} aria-label="chiudi" className="text-[#9A968B] hover:text-[#1C1C1A]">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] font-semibold">Titolo · almeno 15 caratteri</label>
            <input
              type="text"
              required
              minLength={15}
              maxLength={120}
              placeholder="Es. Esportare una lista di clienti come PDF stampabile"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-[#E8E5DE] rounded-md focus:border-[#7C8B6E] focus:outline-none text-[14px]"
              data-testid="propose-title"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] font-semibold">Spiegalo bene · almeno 80 caratteri</label>
            <textarea
              required
              minLength={80}
              rows={5}
              maxLength={2000}
              placeholder="Cosa vorresti? In che situazione ti servirebbe? Più sei concreto, più è facile per il team realizzarla. (Idee generiche o ripetute non vengono approvate.)"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-[#E8E5DE] rounded-md focus:border-[#7C8B6E] focus:outline-none text-[14px] leading-relaxed"
              data-testid="propose-body"
            />
            <div className="text-right text-[10px] text-[#9A968B] mt-1">{form.body.length}/2000{form.body.length < 80 ? ` · servono altri ${80 - form.body.length}` : ""}</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] font-semibold">Area</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setForm({ ...form, category: c.id })}
                  className={`text-[12px] px-2.5 py-1.5 rounded-md transition-colors ${
                    form.category === c.id
                      ? "bg-[#7C8B6E] text-white"
                      : "bg-white border border-[#E8E5DE] hover:border-[#C8C5BD] text-[#1C1C1A]"
                  }`}
                  data-testid={`cat-${c.id}`}
                >{c.label}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-[#FAFAF7] border-t border-[#E8E5DE] flex items-center justify-between gap-3">
          <span className="text-[11.5px] text-[#6B6A65]">
            <strong className="text-amber-700">+5 crediti</strong> alla pubblicazione · <strong className="text-amber-700">+30</strong> se va in sviluppo.
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-[13px] text-[#6B6A65] hover:text-[#1C1C1A]">
              Annulla
            </button>
            <button type="submit" disabled={submitting} className="btn-primary text-sm disabled:opacity-60" data-testid="propose-submit">
              {submitting ? "Invio…" : <>Pubblica idea <Sparkle size={13} weight="fill" /></>}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
